/**
 * CatalogRepository — the single access point for catalog reads.
 *
 * All UI code must go through this interface. Direct imports of mock data
 * (`PRODUCTS`, `COLLECTIONS`) or Cloud server functions from routes/components
 * are considered a boundary violation; future integrations (CMS, search, cache)
 * plug in here without touching the storefront.
 *
 * Source selection is controlled by `VITE_CATALOG_SOURCE` at build time:
 *   - "mock"   → RC1 in-memory catalog (default, zero risk)
 *   - "cloud"  → Lovable Cloud, no fallback
 *   - "hybrid" → Lovable Cloud with automatic fallback to mock on failure
 */
import type { CatalogProduct, CatalogFilters, Collection } from "./products";
import { filterAndSort } from "./products";
import { cloudRepository } from "./repository.cloud";

export type CatalogSource = "mock" | "cloud" | "hybrid";

export interface CatalogRepository {
  readonly source: CatalogSource;
  getProducts(filters?: Partial<CatalogFilters>): Promise<CatalogProduct[]>;
  getProduct(slug: string): Promise<CatalogProduct | undefined>;
  getCategories(): Promise<Array<{ id: string; slug: string; name: string }>>;
  getCollections(): Promise<Collection[]>;
  searchProducts(query: string, opts?: { categoryId?: string; limit?: number }): Promise<CatalogProduct[]>;
  /** All active products for a category (by CategoryRegistry id / slug). */
  getProductsByCategory(categoryId: string, filters?: Partial<CatalogFilters>): Promise<CatalogProduct[]>;
  /** All active products linked to a collection slug (editorial grouping). */
  getProductsByCollection(slug: string): Promise<CatalogProduct[]>;
}

let _repo: CatalogRepository | undefined;
export function getCatalogRepository(): CatalogRepository {
  if (_repo) return _repo;
  _repo = instrument(cloudRepository());
  return _repo;
}

// ---------------------------------------------------------------------------
// Prompt 15 · Lightweight instrumentation (Repository Metrics recommendation)
// ---------------------------------------------------------------------------

type Counter = { calls: number; ms: number; errors: number };
const METRICS = new Map<string, Counter>();

function bump(key: string, ms: number, ok: boolean) {
  const c = METRICS.get(key) ?? { calls: 0, ms: 0, errors: 0 };
  c.calls += 1;
  c.ms += ms;
  if (!ok) c.errors += 1;
  METRICS.set(key, c);
}

export function readCatalogMetrics(): Record<string, Counter> {
  return Object.fromEntries(METRICS);
}

function instrument(repo: CatalogRepository): CatalogRepository {
  const wrap = <A extends unknown[], R>(name: string, fn: (...a: A) => Promise<R>) =>
    async (...args: A): Promise<R> => {
      const t0 = Date.now();
      try {
        const v = await fn(...args);
        bump(`${repo.source}:${name}`, Date.now() - t0, true);
        return v;
      } catch (err) {
        bump(`${repo.source}:${name}`, Date.now() - t0, false);
        throw err;
      }
    };
  return {
    source: repo.source,
    getProducts:            wrap("getProducts", repo.getProducts.bind(repo)),
    getProduct:             wrap("getProduct", repo.getProduct.bind(repo)),
    getCategories:          wrap("getCategories",          repo.getCategories.bind(repo)),
    getCollections:         wrap("getCollections",         repo.getCollections.bind(repo)),
    searchProducts:         wrap("searchProducts", repo.searchProducts.bind(repo)),
    getProductsByCategory:  wrap("getProductsByCategory", repo.getProductsByCategory.bind(repo)),
    getProductsByCollection:wrap("getProductsByCollection", repo.getProductsByCollection.bind(repo)),
  };
}

/** Utility for tests / storybook — bypass the singleton. */
export function makeCatalogRepository(source: CatalogSource): CatalogRepository {
  void source;
  return cloudRepository();
}

/** Shared filter/sort helper so all repositories apply identical semantics. */
export function applyCatalogFilters(products: CatalogProduct[], f?: Partial<CatalogFilters>): CatalogProduct[] {
  if (!f) return products;
  const merged: CatalogFilters = {
    q: f.q ?? "",
    families: f.families ?? [],
    genders: f.genders ?? [],
    concentrations: f.concentrations ?? [],
    collection: f.collection ?? "",
    min: f.min ?? Number.NEGATIVE_INFINITY,
    max: f.max ?? Number.POSITIVE_INFINITY,
    sort: f.sort ?? "featured",
    categoryId: f.categoryId,
    attrs: f.attrs,
    ranges: f.ranges,
    bools: f.bools,
  };
  return filterAndSort(products, merged);
}